import ky from 'ky';
import { z } from 'zod';

const loginResponseSchema = z.object({
  access_token: z.string(),
  token_type: z.string(),
  expires_in: z.number(),
  client_id: z.string(),
  role: z.string(),
});

export type LoginResponse = z.infer<typeof loginResponseSchema>;

export type LoginErrorKind = 'invalid_credentials' | 'network' | 'server';

export class LoginError extends Error {
  constructor(
    public readonly kind: LoginErrorKind,
    message: string,
  ) {
    super(message);
    this.name = 'LoginError';
  }
}

/**
 * `baseUrl` is the single, unified auth endpoint (AUTH_BASE_URL). Every user
 * signs in here regardless of which region's report they end up viewing; the
 * issued JWT's `allowed_reports` drives where they land and the report switcher.
 */
export async function loginApi(baseUrl: string, email: string, password: string): Promise<LoginResponse> {
  let raw: unknown;
  try {
    raw = await ky
      .post(`${baseUrl}/auth/login`, {
        json: { email, password },
        retry: 0,
        timeout: 15_000,
      })
      .json();
  } catch (err: unknown) {
    const status = (err as { response?: { status?: number } })?.response?.status;
    if (status === 401 || status === 400) {
      throw new LoginError('invalid_credentials', 'Invalid email or password');
    }
    if (status && status >= 500) {
      throw new LoginError('server', 'Login service is unavailable. Try again in a moment.');
    }
    throw new LoginError('network', 'Could not reach the login service.');
  }
  return loginResponseSchema.parse(raw);
}
